import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { jsPDF } from "jspdf";
import { Calculator, TrendingUp, Wallet, PiggyBank, Percent, ArrowRight, Download, Trophy } from "lucide-react";
import { navPaths } from "../mock/mock";
import { useLang } from "../i18n/LanguageContext";

const eur = (n) =>
  new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(
    isFinite(n) ? n : 0
  );

const compute = (sc) => {
  const revenue = sc.ticket * sc.orders * sc.days;
  const foodCostVal = (revenue * sc.foodcost) / 100;
  const royaltyVal = (revenue * sc.royalty) / 100;
  const fixed = sc.rent + sc.staff + sc.other;
  const totalCosts = foodCostVal + royaltyVal + fixed;
  const profit = revenue - totalCosts;
  return { revenue, totalCosts, profit, yearly: profit * 12, margin: revenue > 0 ? (profit / revenue) * 100 : 0 };
};

function Slider({ label, value, min, max, step, unit, onChange }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <label className="text-sm font-medium text-[#3B342E]">{label}</label>
        <span className="font-display font-bold text-[#17784A] text-sm">
          {unit === "€" ? eur(value) : unit === "%" ? `${value}%` : value}
        </span>
      </div>
      <input
        type="range" min={min} max={max} step={step} value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full accent-[#17784A] cursor-pointer"
      />
    </div>
  );
}

const DEFAULTS = [
  { name: "Roma", ticket: 7, orders: 140, days: 30, foodcost: 30, rent: 3500, staff: 2000, other: 1500, royalty: 5 },
  { name: "Milano", ticket: 8, orders: 120, days: 30, foodcost: 32, rent: 5000, staff: 2500, other: 1800, royalty: 5 },
];

function ScenarioPanel({ tag, sc, s, onChange }) {
  const res = compute(sc);
  const set = (k) => (v) => onChange({ ...sc, [k]: v });
  return (
    <div className="bg-white rounded-3xl border border-black/5 shadow-sm p-6 lg:p-7">
      <div className="flex items-center gap-3 mb-5">
        <span className="grid place-items-center w-9 h-9 rounded-lg bg-[#17784A] text-white font-display font-bold">{tag}</span>
        <input
          value={sc.name}
          onChange={(e) => onChange({ ...sc, name: e.target.value })}
          aria-label={s.scenarioName}
          className="flex-1 font-display font-bold text-lg text-[#14110F] border-b border-black/10 focus:border-[#17784A] outline-none py-1 transition-colors"
        />
      </div>
      <div className="space-y-4">
        <Slider label={s.ticket} value={sc.ticket} min={4} max={15} step={0.5} unit="€" onChange={set("ticket")} />
        <Slider label={s.orders} value={sc.orders} min={20} max={400} step={5} onChange={set("orders")} />
        <Slider label={s.days} value={sc.days} min={20} max={31} step={1} onChange={set("days")} />
        <Slider label={s.foodcost} value={sc.foodcost} min={20} max={45} step={1} unit="%" onChange={set("foodcost")} />
        <Slider label={s.rent} value={sc.rent} min={500} max={8000} step={100} unit="€" onChange={set("rent")} />
        <Slider label={s.staff} value={sc.staff} min={0} max={8000} step={100} unit="€" onChange={set("staff")} />
        <Slider label={s.other} value={sc.other} min={300} max={5000} step={100} unit="€" onChange={set("other")} />
        <Slider label={s.royalty} value={sc.royalty} min={0} max={10} step={0.5} unit="%" onChange={set("royalty")} />
      </div>
      <div className="mt-6 grid grid-cols-3 gap-3 text-center">
        <div className="rounded-xl bg-[#F3EFE7] p-3">
          <div className="text-[11px] text-[#3B342E]/55">{s.revenue}</div>
          <div className="font-display font-bold text-[#14110F] text-sm mt-0.5">{eur(res.revenue)}</div>
        </div>
        <div className="rounded-xl bg-[#F3EFE7] p-3">
          <div className="text-[11px] text-[#3B342E]/55">{s.totalCosts}</div>
          <div className="font-display font-bold text-[#14110F] text-sm mt-0.5">{eur(res.totalCosts)}</div>
        </div>
        <div className="rounded-xl bg-[#17784A] p-3">
          <div className="text-[11px] text-white/70">{s.profit}</div>
          <div className="font-display font-bold text-white text-sm mt-0.5">{eur(res.profit)}</div>
        </div>
      </div>
    </div>
  );
}

export default function FranchiseSim() {
  const { t } = useLang();
  const s = t.risorice.sim;
  const [scenarios, setScenarios] = useState(DEFAULTS);
  const [logo, setLogo] = useState(null);

  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        canvas.getContext("2d").drawImage(img, 0, 0);
        setLogo({ data: canvas.toDataURL("image/png"), w: img.naturalWidth, h: img.naturalHeight });
      } catch (e) { /* logo optional */ }
    };
    img.src = (process.env.PUBLIC_URL || "") + "/logo-mc.png";
  }, []);

  const resA = useMemo(() => compute(scenarios[0]), [scenarios]);
  const resB = useMemo(() => compute(scenarios[1]), [scenarios]);
  const winner = resA.profit === resB.profit ? -1 : resA.profit > resB.profit ? 0 : 1;

  const update = (idx) => (sc) => setScenarios((prev) => prev.map((x, i) => (i === idx ? sc : x)));

  const downloadPdf = () => {
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const W = 595, M = 40;
    const GREEN = [23, 120, 74], BRIGHT = [34, 181, 115], DARK = [20, 17, 15],
      GREY = [110, 110, 110], WHITE = [255, 255, 255], LINE = [230, 226, 218], BODY = [64, 60, 56];

    // ---- Header band ----
    doc.setFillColor(...GREEN);
    doc.rect(0, 0, W, 96, "F");
    doc.setFillColor(...BRIGHT);
    doc.rect(0, 96, W, 4, "F");

    let textX = M;
    if (logo) {
      const h = 34, w = (logo.w / logo.h) * h;
      const chipW = w + 22, chipH = h + 16;
      doc.setFillColor(...WHITE);
      doc.roundedRect(M, 24, chipW, chipH, 8, 8, "F");
      try { doc.addImage(logo.data, "PNG", M + 11, 32, w, h); } catch (e) { /* skip */ }
      textX = M + chipW + 18;
    }
    doc.setTextColor(...WHITE);
    doc.setFont("helvetica", "bold"); doc.setFontSize(22);
    doc.text("RISORICE\u00ae", textX, 48);
    doc.setFont("helvetica", "normal"); doc.setFontSize(9.5);
    doc.text("MICHELCHEF\u00ae \u00b7 " + s.eyebrow, textX, 65);
    doc.setFontSize(8); doc.setTextColor(214, 234, 224);
    doc.text(new Date().toLocaleString("it-IT"), textX, 80);

    // ---- Section title ----
    let y = 132;
    doc.setTextColor(...DARK); doc.setFont("helvetica", "bold"); doc.setFontSize(13);
    doc.text(s.title, M, y);

    // ---- Scenario cards side by side ----
    const cardY = y + 16, cardW = 250, cardH = 300, gap = 15;
    const cols = [M, M + cardW + gap];
    const headerColors = [GREEN, DARK];

    scenarios.forEach((sc, idx) => {
      const res = compute(sc);
      const x = cols[idx];
      doc.setFillColor(...WHITE); doc.setDrawColor(...LINE);
      doc.roundedRect(x, cardY, cardW, cardH, 10, 10, "FD");
      doc.setFillColor(...headerColors[idx]);
      doc.roundedRect(x, cardY, cardW, 34, 10, 10, "F");
      doc.rect(x, cardY + 18, cardW, 16, "F");
      doc.setTextColor(...WHITE); doc.setFont("helvetica", "bold"); doc.setFontSize(11);
      doc.text(`${idx === 0 ? "A" : "B"}  \u00b7  ${sc.name}`, x + 14, cardY + 22);
      if (winner === idx) {
        const bw = 68;
        doc.setFillColor(...BRIGHT);
        doc.roundedRect(x + cardW - bw - 12, cardY + 9, bw, 16, 8, 8, "F");
        doc.setTextColor(...DARK); doc.setFontSize(7.5);
        doc.text(String(s.better).toUpperCase(), x + cardW - bw / 2 - 12, cardY + 20, { align: "center" });
      }

      let ry = cardY + 52;
      doc.setFont("helvetica", "bold"); doc.setFontSize(8); doc.setTextColor(...GREY);
      doc.text(String(s.inputsLabel).toUpperCase(), x + 14, ry); ry += 13;
      doc.setFont("helvetica", "normal"); doc.setTextColor(...BODY); doc.setFontSize(8.5);
      const inputs = [
        [s.ticket, eur(sc.ticket)], [s.orders, String(sc.orders)], [s.days, String(sc.days)],
        [s.foodcost, sc.foodcost + "%"], [s.rent, eur(sc.rent)], [s.staff, eur(sc.staff)],
        [s.other, eur(sc.other)], [s.royalty, sc.royalty + "%"],
      ];
      inputs.forEach(([k, v]) => {
        doc.text(String(k), x + 14, ry);
        doc.text(String(v), x + cardW - 14, ry, { align: "right" });
        ry += 13.5;
      });
      ry += 2;
      doc.setDrawColor(...LINE); doc.line(x + 14, ry, x + cardW - 14, ry); ry += 15;
      doc.setFont("helvetica", "bold"); doc.setFontSize(8); doc.setTextColor(...GREY);
      doc.text(String(s.resultsLabel).toUpperCase(), x + 14, ry); ry += 14;
      const outs = [
        [s.revenue, eur(res.revenue), BODY], [s.totalCosts, eur(res.totalCosts), BODY],
        [s.margin, res.margin.toFixed(0) + "%", BODY],
        [s.profit, eur(res.profit), GREEN], [s.yearly, eur(res.yearly), GREEN],
      ];
      doc.setFontSize(9);
      outs.forEach(([k, v, col]) => {
        doc.setFont("helvetica", "normal"); doc.setTextColor(...BODY);
        doc.text(String(k), x + 14, ry);
        doc.setFont("helvetica", "bold"); doc.setTextColor(...col);
        doc.text(String(v), x + cardW - 14, ry, { align: "right" });
        ry += 15;
      });
    });

    // ---- Comparison summary ----
    const sy = cardY + cardH + 22;
    doc.setFillColor(...DARK);
    doc.roundedRect(M, sy, W - 2 * M, 62, 10, 10, "F");
    doc.setTextColor(...BRIGHT); doc.setFont("helvetica", "bold"); doc.setFontSize(9);
    doc.text(String(s.summaryTitle).toUpperCase(), M + 18, sy + 22);
    doc.setTextColor(...WHITE); doc.setFontSize(12);
    const delta = Math.abs(resA.profit - resB.profit);
    const summaryLine = winner === -1
      ? String(s.tie)
      : `${scenarios[winner].name} ${s.winnerMsg}: +${eur(delta)}${s.perMo}  \u00b7  +${eur(delta * 12)}${s.perYr}`;
    doc.text(summaryLine, M + 18, sy + 44);

    // ---- Footer ----
    doc.setFont("helvetica", "normal"); doc.setFontSize(7.5); doc.setTextColor(150, 150, 150);
    doc.text(String(s.note), M, 812, { maxWidth: W - 2 * M });
    doc.setTextColor(...GREEN); doc.setFont("helvetica", "bold"); doc.setFontSize(8);
    doc.text("www.michelchef.it  \u00b7  risorice.it", M, 828);

    doc.save("risorice-simulazione.pdf");
  };

  return (
    <section className="py-24 max-w-7xl mx-auto px-5 lg:px-8">
      <div className="reveal text-center max-w-2xl mx-auto">
        <span className="inline-flex items-center gap-2 text-[#D8382E] font-semibold text-sm uppercase tracking-wider">
          <Calculator size={15} /> {s.eyebrow}
        </span>
        <h2 className="font-display font-extrabold text-3xl lg:text-4xl mt-3 text-[#14110F]">{s.title}</h2>
        <p className="mt-4 text-[#3B342E]/75 text-lg">{s.subtitle}</p>
      </div>

      {/* Two scenarios */}
      <div className="reveal mt-12 grid lg:grid-cols-2 gap-6">
        <ScenarioPanel tag="A" sc={scenarios[0]} s={s} onChange={update(0)} />
        <ScenarioPanel tag="B" sc={scenarios[1]} s={s} onChange={update(1)} />
      </div>

      {/* Comparison strip */}
      <div className="reveal mt-6 rounded-3xl bg-[#14110F] text-white p-6 lg:p-8">
        <div className="flex items-center gap-2 text-white/70 text-sm uppercase tracking-wider mb-5">
          <Percent size={15} /> {s.compareTitle}
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          {[{ sc: scenarios[0], res: resA, i: 0 }, { sc: scenarios[1], res: resB, i: 1 }].map(({ sc, res, i }) => (
            <div key={i} className={`rounded-2xl p-6 border ${winner === i ? "bg-[#17784A] border-[#22B573]" : "bg-white/5 border-white/10"}`}>
              <div className="flex items-center justify-between">
                <span className="font-display font-bold text-lg">{i === 0 ? "A" : "B"} · {sc.name}</span>
                {winner === i && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-white/20 px-2.5 py-1 text-xs font-semibold"><Trophy size={12} /> {s.better}</span>
                )}
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3">
                <div><div className="text-white/60 text-xs">{s.profit}</div><div className="font-display font-extrabold text-2xl">{eur(res.profit)}</div></div>
                <div><div className="text-white/60 text-xs">{s.margin}</div><div className="font-display font-extrabold text-2xl">{res.margin.toFixed(0)}%</div></div>
                <div><div className="text-white/60 text-xs">{s.yearly}</div><div className="font-display font-bold text-lg text-[#22B573]">{eur(res.yearly)}</div></div>
                <div><div className="text-white/60 text-xs">{s.revenue}</div><div className="font-display font-bold text-lg">{eur(res.revenue)}</div></div>
              </div>
            </div>
          ))}
        </div>
        <p className="mt-5 text-white/40 text-xs leading-relaxed">{s.note}</p>
        <div className="mt-6 flex flex-wrap gap-4">
          <button onClick={downloadPdf} className="btn-press inline-flex items-center gap-2 rounded-full bg-[#22B573] text-[#083a24] px-7 py-3.5 font-semibold hover:bg-[#1ba065]">
            <Download size={18} /> {s.download}
          </button>
          <Link to={navPaths.contact} className="btn-press inline-flex items-center gap-2 rounded-full bg-white text-[#14110F] px-7 py-3.5 font-semibold hover:bg-[#FBF9F4]">
            {s.cta} <ArrowRight size={18} />
          </Link>
        </div>
      </div>
    </section>
  );
}
